import React, { useEffect } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import { Toaster } from '@repo/videa-components/src/ui/sonner';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isExternalIFrame } from '@/utils/iframe';
import { useTrackEvent } from '@/utils/mixpanel';

import { Header } from '../layout/header/Header';
import { resetImagesState } from '../modules/annotator/AnnotatorActions';
import { useIsEmbedded } from '../utils/hooks';
import { sendException } from '../utils/sentry';

import { NewVersionPopup } from './snackbar/NewVersionPopup';
import SnackbarContainer from './snackbar/SnackbarContainer';

import './App.css';

const App = (props: {
  children: React.ReactNode;
  history: any;
  isLoggedIn: boolean;
  user: any;
}) => {
  const { children, isLoggedIn, user } = props;
  const isExternalIframe = isExternalIFrame();
  const oktaAuth = useOktaAuth().oktaAuth || null;
  const authClient = !!oktaAuth && oktaAuth;
  const { authState } = authClient as any;
  const practice = useSelector((state: any) => state.login?.user?.practice);
  const isEmbedded = useIsEmbedded();
  const dispatch = useDispatch();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    if (user?.username) {
      Sentry.setUser({
        username: user.username,
        email: user.username,
        practice_id: user.primaryPracticeId,
        practice_name: user.primaryPracticeName,
        org_id: user.primaryOrganizationId,
        role: user.userRole,
        is_embedded: isEmbedded,
        is_ddai: practice?.configuration === 'DDAI',
      });
      Sentry.setTag('practice_id', user.primaryPracticeId);
      Sentry.setTag('organization_id', user.primaryOrganizationId);
      Sentry.setTag('is_embedded', isEmbedded);
      Sentry.setTag('is_ddai', practice?.configuration === 'DDAI');
    }
  }, [isEmbedded, practice?.configuration, user]);

  useEffect(() => {
    // send any errors related to refreshing the okta token
    oktaAuth.tokenManager.on('error', (err: any) => {
      // commenting for now cause inflating MTUs
      // trackEvent(mixpanelEvents.oktaTokenManagerError, {
      //   error: err,
      // });
      sendException(err, { tags: [['action', 'OKTA_TOKEN_MANAGER_ERROR']] });
    });

    // eslint-disable-next-line @typescript-eslint/no-shadow -- 'authState' is already declared in the upper scope on line 262 column 9.
    oktaAuth.authStateManager.subscribe((authState: any) => {
      // handle emitted latest authState
      if (!authState.isAuthenticated) {
        dispatch(resetImagesState());
      }
    });

    /** Uncomment this line to test a okta token refresh, this will run 10s after the page loads */
    // setTimeout(() => {
    //   oktaAuth.tokenManager.remove('idToken');
    // }, 10000);
  }, [
    authState,
    dispatch,
    oktaAuth.authStateManager,
    oktaAuth.tokenManager,
    trackEvent,
  ]);

  return (
    <>
      <div
        className={
          isLoggedIn || isExternalIframe || !isLoginPage
            ? 'videaContainer'
            : 'videaContainer backgroundBranding'
        }
      >
        <Header />
        <NewVersionPopup isLoggedIn={isLoggedIn} user={user} />
        {children}
      </div>
      <SnackbarContainer />
      <Toaster position="top-right" />
    </>
  );
};

export default App;
